import { publicRegionsFor, type RegionCode, type RuntimeEnvironment } from '@planner/config';
import { useState, type ReactNode } from 'react';
import { fetchProductCatalogue } from '../lib/catalogue.js';
import { useComingSoonFlag } from '../lib/coming-soon.js';
import { brand } from '../lib/env.js';
import { useRegionAvailability } from '../lib/region-availability.js';

interface SiteGateProps {
  regionCode: RegionCode;
  environment: RuntimeEnvironment;
  children: ReactNode;
}

function ComingSoon() {
  return (
    <main
      data-name="coming-soon-screen"
      className="mx-auto flex w-full max-w-3xl flex-1 flex-col items-center px-4 py-20 text-center sm:py-24"
    >
      <h1 className="text-2xl font-semibold sm:text-3xl">Coming soon</h1>
      <p className="mt-4 max-w-md text-sm text-gray-600">
        {brand.name} is putting the finishing touches on this planner. Check back soon.
      </p>
    </main>
  );
}

function ComingSoonFlagGate({ regionCode, environment, children }: SiteGateProps) {
  const state = useComingSoonFlag(regionCode, environment);
  if (state === 'checking') {
    return (
      <main
        data-name="site-gate-loading"
        aria-busy="true"
        className="mx-auto flex w-full max-w-3xl flex-1 justify-center px-4 py-20 text-sm text-gray-600"
      >
        Loading planner…
      </main>
    );
  }
  if (state === 'held') {
    return <ComingSoon />;
  }
  return <RegionApiGate>{children}</RegionApiGate>;
}

function RegionApiGate({ children }: { children: ReactNode }) {
  const unavailable = useRegionAvailability((state) => state.unavailable);
  const [retrying, setRetrying] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);

  const retry = async () => {
    setRetrying(true);
    setRetryFailed(false);
    try {
      await fetchProductCatalogue();
      useRegionAvailability.getState().setUnavailable(false);
    } catch {
      setRetryFailed(true);
    } finally {
      setRetrying(false);
    }
  };

  if (!unavailable) return <>{children}</>;
  return (
    <main
      data-name="region-unavailable-screen"
      className="mx-auto flex w-full max-w-3xl flex-1 flex-col items-center px-4 py-20 text-center sm:py-24"
    >
      <h1 className="text-2xl font-semibold sm:text-3xl">Region unavailable</h1>
      <p className="mt-4 max-w-md text-sm text-gray-600">
        This planner is not available in your region right now. Please try again later.
      </p>
      {retryFailed && (
        <p role="alert" className="mt-4 text-sm text-red-700">
          The planner is still unavailable. Please retry.
        </p>
      )}
      <button
        type="button"
        data-name="region-unavailable-retry"
        disabled={retrying}
        onClick={() => void retry()}
        className="mt-6 min-h-11 rounded-xl bg-brand px-5 text-sm font-semibold text-brand-foreground disabled:opacity-50"
      >
        {retrying ? 'Checking…' : 'Retry'}
      </button>
    </main>
  );
}

export function SiteGate({ regionCode, environment, children }: SiteGateProps) {
  if (!publicRegionsFor(environment).includes(regionCode)) {
    return <ComingSoon />;
  }
  return (
    <ComingSoonFlagGate key={`${environment}-${regionCode}`} {...{ regionCode, environment }}>
      {children}
    </ComingSoonFlagGate>
  );
}
